import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import MssModal from '../../common/MssModal';
import Loader from '../../common/Loader';
import UnsavedChangesWarning from '../../common/UnsavedChangesWarning';
import AIResponseError from '../../common/AIResponseError';
import BreadcrumbCrumb from '../../common/Breadcrumbs/BreadcrumbCrumb';
import useUnsavedChangesGuard from '../../common/useUnsavedChangesGuard';
import PageBreadcrumbs from '../../common/Breadcrumbs/PageBreadcrumbs';
import { TruncatedTitle } from '../../common/TruncatedTitle';
import EditSummaryHeader from './EditSummaryHeader';
import EditSummaryBody from './EditSummaryBody';
import { patchData } from '../../../DataAccessLayer';
import { summaries } from '../../../DataAccessLayer/services';
import { sanitizeSummaryHtml } from '../../common/helpers';

// Serializes through the DOM so the baseline matches the editor's own
// innerHTML output; whitespace/attribute-quoting differences are not edits.
const normalizeHtml = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html ?? '';
  return container.innerHTML;
};

function EditSummary() {
  const { summaryId } = useParams();
  const navigate = useNavigate();
  const isImpersonating = useSelector((state) => state.impersonation.isImpersonating);
  const courseId = useSelector((state) => state.course.courseId);
  const showBanner = isImpersonating || !!courseId;
  const [isSaving, setIsSaving] = useState(false);
  // When the editor last saved, which it reports in place of the warning.
  const [savedAt, setSavedAt] = useState(null);

  const { summary, setSummary, isLoading, loadError } = useOutletContext();

  const [title, setTitle] = useState(summary?.title ?? '');
  const [editorContent, setEditorContent] = useState(null);
  const baselineHTML = useRef('');

  useEffect(() => {
    if (summary && editorContent === null) {
      setTitle(summary.title);
      // Cleaned here as well as by the backend on save, so the page doesn't
      // rely on the stored HTML being clean. It is cleaned before anything
      // parses it, because parsing alone can run code: both
      // normalizeHtml and the editor parse it in the page, and the browser starts
      // loading images straight away, so <img src="x" onerror="..."> would run
      // its onerror code without the user doing anything. The baseline is built
      // from the same cleaned HTML the editor receives, so opening a summary
      // without editing it never shows as an unsaved change.
      const content = sanitizeSummaryHtml(summary.content);
      setEditorContent(content);
      baselineHTML.current = normalizeHtml(content);
    }
  }, [summary, editorContent]);

  const isDirty =
    !!summary &&
    editorContent !== null &&
    (title !== summary.title || normalizeHtml(editorContent) !== baselineHTML.current);

  // Edits live in the page until Save is pressed, so leaving throws them away.
  const { isPromptOpen, guardNavigation, navigateAway, stay, leave } = useUnsavedChangesGuard({
    isDirty,
    backPath: '..',
  });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // The backend sanitizes again before saving; doing it here as well means
      // the editor can be updated to exactly what was saved (below).
      const content = sanitizeSummaryHtml(editorContent);
      const updatedSummary = await patchData(summaries({ id: summaryId, courseId }), {
        title: title.trim(),
        content,
      });
      setSummary(updatedSummary);
      // The editor shows what was saved, so anything the cleaning removed
      // doesn't linger on screen or count as an unsaved change.
      setEditorContent(content);
      baselineHTML.current = normalizeHtml(content);
      toast.success('Summary note updated.');
      setSavedAt(new Date());
    } catch (err) {
      console.log(err);
      toast.error('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const isSaveDisabled = isSaving || !isDirty || !title.trim();

  return (
    <Box className={'mss__editSummary' + (showBanner ? ' impersonating' : '')} id="editSummary">
      <PageBreadcrumbs
        id="editSummary__nav_breadcrumbs"
        rootTo="../../.."
        isVisible={!loadError}
        onRootClick={(event) => {
          event.preventDefault();
          guardNavigation('../../..');
        }}
      >
        <BreadcrumbCrumb
          isLoading={isLoading}
          to=".."
          width={100}
          onClick={(event) => {
            event.preventDefault();
            guardNavigation('..');
          }}
        >
          <TruncatedTitle name={summary?.title} isFocusable={false} variant="inherit" />
        </BreadcrumbCrumb>
        <BreadcrumbCrumb isLoading={isLoading} width={140}>
          {summary && (
            <>
              Edit <TruncatedTitle name={summary.title} variant="inherit" />
            </>
          )}
        </BreadcrumbCrumb>
      </PageBreadcrumbs>

      {loadError && (
        <AIResponseError
          message={loadError}
          onGoToDashboard={() => navigate('../../..', { relative: 'path' })}
        />
      )}

      {!loadError && (
        <>
          <EditSummaryHeader
            summary={summary ?? {}}
            isLoading={isLoading}
            isDirty={isDirty}
            isSaveDisabled={isSaveDisabled}
            onSave={handleSave}
            savedAt={savedAt}
            onDeleted={() => navigateAway('../../..')}
          />

          <UnsavedChangesWarning
            isDirty={isDirty}
            savedAt={savedAt}
            className="unsavedWarningMobile"
          />

          <Box className="mss__box editSummaryContent" id="editSummary__box_content">
            <EditSummaryBody
              isLoading={isLoading}
              isReady={editorContent !== null}
              title={title}
              onTitleChange={setTitle}
              editorContent={editorContent}
              onEditorChange={setEditorContent}
            />
          </Box>
        </>
      )}

      {!isLoading && !loadError && (
        <>
          <Loader id="editSummary__loader_saving" isOpen={isSaving} />

          <MssModal
            isOpen={isPromptOpen}
            onClose={stay}
            title="Unsaved Changes"
            id="editSummary__modal_unsavedChanges"
            primaryButtonTitle="Stay"
            onPrimaryClick={stay}
            secondaryButtonTitle="Leave"
            onSecondaryClick={leave}
          >
            <Typography>Unsaved edits will not be applied. Do you wish to leave?</Typography>
          </MssModal>
        </>
      )}
    </Box>
  );
}

export default EditSummary;
